import type { ReactNode } from 'react';
import { useGetIdentity, useLogout } from '@refinedev/core';
import { useLocation } from 'react-router';
import { LogOut, Moon, Sun } from 'lucide-react';
import { resources } from '@/resources';
import { useTheme } from '@/lib/theme';
import { Sidebar } from './Sidebar';
import { extensionDisplayName } from '@/extensions/extension-nav';

// Admin chrome: full-height sidebar (brand + nav + identity) with a slim
// context bar over the content. The shared <TrackframeHeader /> stays on the
// hub root (HubLayout); the admin subtree owns its own header so it can be
// fully token-themed.
export function AdminLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = { show: 'Details', edit: 'Edit', create: 'New' };

function breadcrumb(pathname: string): string[] {
  const segments = pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
  if (segments.length === 0) return ['Dashboard'];
  // HUB-103 — /admin/ext/<name>[/settings] is not a refine resource.
  if (segments[0] === 'ext' && segments[1]) {
    return ['Extensions', extensionDisplayName(segments[1]), ...(segments[2] === 'settings' ? ['Settings'] : [])];
  }
  const resource = resources.find((r) => r.name === segments[0]);
  const parts = [String(resource?.meta?.label ?? segments[0])];
  if (segments[1] && ACTION_LABELS[segments[1]]) parts.push(ACTION_LABELS[segments[1]]);
  return parts;
}

function TopBar() {
  const location = useLocation();
  const { mode, setMode } = useTheme();
  const { data: identity } = useGetIdentity<{ email?: string; name?: string | null }>();
  const { mutate: logout } = useLogout();
  const parts = breadcrumb(location.pathname);

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b bg-card px-5">
      <nav aria-label="Breadcrumb" className="min-w-0 truncate text-sm text-muted-foreground">
        {parts.map((part, i) => (
          <span key={part}>
            {i > 0 && <span className="mx-1.5 text-muted-foreground/60">/</span>}
            <span className={i === parts.length - 1 ? 'font-semibold text-foreground' : undefined}>
              {part}
            </span>
          </span>
        ))}
      </nav>
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          className="flex size-8 cursor-pointer items-center justify-center rounded-[var(--radius-ctl)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
          aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {mode === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
        {identity?.email && (
          <span className="hidden max-w-56 truncate px-2 text-xs text-muted-foreground sm:block">
            {identity.name || identity.email}
          </span>
        )}
        <button
          type="button"
          className="flex size-8 cursor-pointer items-center justify-center rounded-[var(--radius-ctl)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => logout()}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </header>
  );
}
